import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { crmApi } from '@/api'
import type { CrmLeadCardInFunnelData } from '@/api/types/crm/crmFunnels.types'
import { SETTINGS_TYPES } from '@/constant'
import { SettingsService } from '@/services/SettingsService'
import { getShoppingCurrentLeadFunnelId } from '../leadFunnelId.utils'
import {
  buildLeadAfterModalRefetch,
  fetchLeadCardInFunnelAndPutSnapshot,
  mergeLeadProfileFromGetLeadOnly,
  parseFullLeadFromGetLeadResponse,
  resolveFunnelContextFromLeadCard,
  toValidFunnelId,
  type LeadCardApiData,
} from '../leadModalRefetchMerge'
import type { LeadProfileLead } from '../LeadProfileModal.types'
import { withLeadVkChatFromPayload } from '../../../utils/leadVkChatMeta'
import { patchLeadVkChatMeta } from '../../../utils/leadFields'
import { LeadRefetchContext } from './leadRefetch.context'
import type {
  LeadRefetchContextType,
  LeadRefetchInModalOptions,
  LeadRefetchProviderProps,
} from './leadRefetch.types'
import {
  deriveCommittedFunnelSlice,
  deriveSelectedFunnelIdFromLead,
  extractVkMetaFromGetLeadResponse,
  getLeadFunnelRowId,
  isCrmSettingEnabled,
  parsePositiveId,
} from './leadRefetch.utils'
import { useLeadRefetchRealtime } from './useLeadRefetchRealtime'

export const LeadRefetchProvider: React.FC<LeadRefetchProviderProps> = ({
  children,
  leadId,
  setLead,
  lead,
  open,
  initialFunnelIdForRefetch,
  setInitialFunnelIdForRefetch,
}) => {
  const [needRefetchLeads, setNeedRefetchLeads] = useState(false)
  const [isRefetchingLeadCard, setIsRefetchingLeadCard] = useState(false)
  const [selectedFunnelId, setSelectedFunnelId] = useState<number | null>(() =>
    deriveSelectedFunnelIdFromLead(lead),
  )
  const selectedFunnelIdRef = useRef<number | null>(
    deriveSelectedFunnelIdFromLead(lead),
  )
  const [multipleDealsEnabled, setMultipleDealsEnabled] = useState(false)
  const multipleDealsEnabledRef = useRef(false)
  /** Не стартуем bootstrap, пока не знаем multipleDeals — иначе двойной refetch. */
  const [crmSettingsReady, setCrmSettingsReady] = useState(false)
  const [selectedLeadFunnelId, setSelectedLeadFunnelId] = useState<number | null>(
    null,
  )
  const selectedLeadFunnelIdRef = useRef<number | null>(null)
  /**
   * Сделка, с которой открыли карточку (seed таблицы).
   * Не перетирается merge после refetch — чтобы не мигало и не сбрасывало выбор.
   */
  const openIntentLeadFunnelIdRef = useRef<number | null>(null)
  const bootstrapRefetchKeyRef = useRef('')
  const cardFunnelDealFetchKeyRef = useRef('')

  const [leadCardSnapshot, setLeadCardSnapshot] = useState<LeadCardApiData | null>(
    null,
  )
  const leadCardSnapshotRef = useRef<LeadCardApiData | null>(null)
  leadCardSnapshotRef.current = leadCardSnapshot
  const [cardFunnelSnapshots, setCardFunnelSnapshots] = useState<
    Partial<Record<number, CrmLeadCardInFunnelData>>
  >({})
  const [cardFunnelSnapshotEpoch, setCardFunnelSnapshotEpoch] = useState(0)

  const putCardInFunnelSnapshot = useCallback(
    (funnelId: number, data: CrmLeadCardInFunnelData) => {
      if (!Number.isFinite(funnelId) || funnelId <= 0) return
      setCardFunnelSnapshots((prev) => ({ ...prev, [funnelId]: data }))
      setCardFunnelSnapshotEpoch((e) => e + 1)
    },
    [],
  )

  const getCardInFunnelSnapshot = useCallback(
    (funnelId: number, leadFunnelId?: number | null) => {
      if (!Number.isFinite(funnelId) || funnelId <= 0) return undefined
      const snap = cardFunnelSnapshots[funnelId]
      if (!snap) return undefined

      if (
        leadFunnelId != null &&
        Number.isFinite(Number(leadFunnelId)) &&
        Number(leadFunnelId) > 0
      ) {
        const snapDealId = Number(snap.lead_funnel_id)
        // Нет/другой lead_funnel_id — это снимок другой сделки в той же воронке.
        if (!Number.isFinite(snapDealId) || snapDealId <= 0) return undefined
        if (snapDealId !== Number(leadFunnelId)) return undefined
      }

      return snap
    },
    [cardFunnelSnapshots],
  )

  useEffect(() => {
    let cancelled = false

    const loadMultipleDealsSetting = async () => {
      let settings = await SettingsService.getCorrected(SETTINGS_TYPES.CRM)

      if (
        settings &&
        !('generalCRMSettings_multipleDealsEnabled' in settings)
      ) {
        localStorage.removeItem(SETTINGS_TYPES.CRM)
        settings = await SettingsService.getCorrected(SETTINGS_TYPES.CRM)
      }

      if (cancelled || !settings) {
        if (!cancelled) setCrmSettingsReady(true)
        return
      }

      const enabled = isCrmSettingEnabled(
        settings.generalCRMSettings_multipleDealsEnabled,
      )
      multipleDealsEnabledRef.current = enabled
      setMultipleDealsEnabled(enabled)
      setCrmSettingsReady(true)
    }

    void loadMultipleDealsSetting()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!leadId) setNeedRefetchLeads(false)
  }, [leadId])

  useEffect(() => {
    setLeadCardSnapshot(null)
    leadCardSnapshotRef.current = null
    setCardFunnelSnapshots({})
    setCardFunnelSnapshotEpoch(0)
    selectedLeadFunnelIdRef.current = null
    setSelectedLeadFunnelId(null)
    openIntentLeadFunnelIdRef.current = null
    bootstrapRefetchKeyRef.current = ''
    cardFunnelDealFetchKeyRef.current = ''
  }, [leadId])

  useEffect(() => {
    if (!lead) {
      setSelectedFunnelId(null)
      selectedFunnelIdRef.current = null
      return
    }

    const validFunnelIds = new Set<number>()
    for (const f of lead.funnels ?? []) {
      const id = Number(f.id)
      if (Number.isFinite(id) && id > 0) validFunnelIds.add(id)
    }
    if (lead.funnel?.id) {
      const id = Number(lead.funnel.id)
      if (Number.isFinite(id) && id > 0) validFunnelIds.add(id)
    }

    const fromLead = deriveSelectedFunnelIdFromLead(lead)

    setSelectedFunnelId((prev) => {
      const next =
        prev !== null && validFunnelIds.has(prev) ? prev : fromLead
      selectedFunnelIdRef.current = next
      return next
    })
  }, [lead])

  /** Синхронизация активной сделки с current_funnels / выбранной воронкой. */
  useEffect(() => {
    if (!crmSettingsReady) return

    if (!multipleDealsEnabled) {
      selectedLeadFunnelIdRef.current = null
      setSelectedLeadFunnelId(null)
      return
    }

    const list = leadCardSnapshot?.current_funnels
    const currentFunnels = Array.isArray(list) ? list : []
    const intentDealId = openIntentLeadFunnelIdRef.current
    const leadFunnelIdFromLead = parsePositiveId(
      (lead as { lead_funnel_id?: number | null } | undefined)?.lead_funnel_id,
    )
    const preferredDealId = intentDealId ?? leadFunnelIdFromLead

    setSelectedLeadFunnelId((prev) => {
      const stillValid =
        prev != null &&
        currentFunnels.some((funnel) => {
          if (getLeadFunnelRowId(funnel) !== prev) return false
          if (selectedFunnelId == null) return true
          return Number(funnel.funnel_id) === Number(selectedFunnelId)
        })

      if (stillValid) {
        selectedLeadFunnelIdRef.current = prev
        return prev
      }

      if (
        prev != null &&
        isRefetchingLeadCard &&
        !currentFunnels.some((funnel) => getLeadFunnelRowId(funnel) === prev)
      ) {
        selectedLeadFunnelIdRef.current = prev
        return prev
      }

      const fromPreferredRow =
        preferredDealId != null
          ? currentFunnels.find((funnel) => {
              if (getLeadFunnelRowId(funnel) !== preferredDealId) return false
              if (selectedFunnelId == null) return true
              return Number(funnel.funnel_id) === Number(selectedFunnelId)
            })
          : undefined

      const preferred =
        fromPreferredRow ??
        (selectedFunnelId != null
          ? currentFunnels.find(
              (funnel) => Number(funnel.funnel_id) === Number(selectedFunnelId),
            )
          : undefined)

      const nextId = preferred
        ? getLeadFunnelRowId(preferred)
        : preferredDealId != null &&
            (currentFunnels.length === 0 ||
              currentFunnels.some(
                (funnel) => getLeadFunnelRowId(funnel) === preferredDealId,
              ))
          ? preferredDealId
          : currentFunnels[0]
            ? getLeadFunnelRowId(currentFunnels[0])
            : preferredDealId

      selectedLeadFunnelIdRef.current = nextId
      return nextId
    })
  }, [
    crmSettingsReady,
    multipleDealsEnabled,
    leadCardSnapshot,
    selectedFunnelId,
    isRefetchingLeadCard,
    lead?.lead_funnel_id,
  ])

  /** Один раз зафиксировать сделку открытия из seed (не с каждого merge lead). */
  useEffect(() => {
    if (!open) {
      openIntentLeadFunnelIdRef.current = null
      return
    }
    if (!crmSettingsReady || !multipleDealsEnabled) return
    if (openIntentLeadFunnelIdRef.current != null) return

    const fromLead = parsePositiveId(
      (lead as { lead_funnel_id?: number | null } | undefined)?.lead_funnel_id,
    )
    if (fromLead == null) return

    openIntentLeadFunnelIdRef.current = fromLead
    selectedLeadFunnelIdRef.current = fromLead
    setSelectedLeadFunnelId(fromLead)
  }, [open, crmSettingsReady, multipleDealsEnabled, leadId, lead?.lead_funnel_id])

  const resolvedInitialFunnelId = useMemo(() => {
    const fromProp = toValidFunnelId(initialFunnelIdForRefetch)
    if (fromProp !== null) return fromProp

    const fromLeadCurrent = toValidFunnelId(lead?.currentFunnelId)
    if (fromLeadCurrent !== null) return fromLeadCurrent

    return toValidFunnelId(lead?.funnels?.[0]?.id)
  }, [initialFunnelIdForRefetch, lead?.currentFunnelId, lead?.funnels])

  const handleRefetchLeadInModal = useCallback(
    async (
      funnelId?: number | null,
      options?: LeadRefetchInModalOptions,
    ): Promise<LeadProfileLead | undefined> => {
      const numericLeadId = Number(leadId)
      if (!Number.isFinite(numericLeadId) || numericLeadId <= 0) {
        return undefined
      }
      const silent = options?.silent === true
      if (!silent) {
        setIsRefetchingLeadCard(true)
      }
      let refetchedLead: LeadProfileLead | undefined
      try {
        const requestedFunnelId = toValidFunnelId(funnelId)

        const [cardResponse, leadDetailsResponse] = await Promise.all([
          crmApi.leads.getLeadCard({ lead_id: numericLeadId }),
          crmApi.getLead({ lead_id: numericLeadId }),
        ])

        const cardData = cardResponse?.data?.data as LeadCardApiData | undefined
        const fullLeadFromDetails =
          parseFullLeadFromGetLeadResponse(leadDetailsResponse)

        if (!cardData) {
          if (fullLeadFromDetails) {
            setLead((prev) => {
              if (!prev) {
                return prev
              }
              refetchedLead = mergeLeadProfileFromGetLeadOnly(
                prev,
                fullLeadFromDetails,
              )
              return refetchedLead
            })
          }
          return refetchedLead
        }

        setLeadCardSnapshot(cardData)
        leadCardSnapshotRef.current = cardData

        const { currentFunnels, resolvedFunnelId, rowForResolved } =
          resolveFunnelContextFromLeadCard(cardData, requestedFunnelId)

        const preferredLeadFunnelId =
          multipleDealsEnabledRef.current
            ? selectedLeadFunnelIdRef.current
            : null
        const rowForPreferredDeal =
          preferredLeadFunnelId != null
            ? currentFunnels.find(
                (item) => getLeadFunnelRowId(item) === preferredLeadFunnelId,
              )
            : undefined
        const rowForLeadMerge = rowForPreferredDeal ?? rowForResolved

        let managerIdFromCardFunnel: number | null | undefined = undefined
        if (resolvedFunnelId !== null) {
          const leadFunnelIdForFetch =
            multipleDealsEnabledRef.current &&
            preferredLeadFunnelId != null &&
            Number.isFinite(preferredLeadFunnelId) &&
            preferredLeadFunnelId > 0
              ? preferredLeadFunnelId
              : undefined

          managerIdFromCardFunnel = await fetchLeadCardInFunnelAndPutSnapshot({
            numericLeadId,
            resolvedFunnelId,
            leadFunnelId: leadFunnelIdForFetch,
            putCardInFunnelSnapshot,
          })
        }

        setLead((prev) => {
          if (!prev) {
            return prev
          }
          refetchedLead = buildLeadAfterModalRefetch({
            prev,
            fullLeadFromDetails,
            cardData,
            resolvedFunnelId,
            rowForResolved: rowForLeadMerge,
            managerIdFromCardFunnel,
            currentFunnels,
          })
          return refetchedLead
        })
        return refetchedLead
      } catch (error) {
        console.error('Failed to refetch lead:', error)
        return undefined
      } finally {
        if (!silent) {
          setIsRefetchingLeadCard(false)
        }
      }
    },
    [leadId, setLead, putCardInFunnelSnapshot],
  )

  const handleRefetchRef = useRef(handleRefetchLeadInModal)
  handleRefetchRef.current = handleRefetchLeadInModal

  /** После включения deals / первичного выбора сделки — card-funnel с lead_funnel_id. */
  useEffect(() => {
    if (!open || !crmSettingsReady || !multipleDealsEnabled) {
      return
    }
    const numericLeadId = Number(leadId)
    if (
      !Number.isFinite(numericLeadId) ||
      numericLeadId <= 0 ||
      selectedFunnelId == null ||
      selectedLeadFunnelId == null
    ) {
      return
    }
    const key = `${numericLeadId}:${selectedFunnelId}:${selectedLeadFunnelId}`
    if (cardFunnelDealFetchKeyRef.current === key) return
    cardFunnelDealFetchKeyRef.current = key
    void handleRefetchRef.current?.(selectedFunnelId)
  }, [
    open,
    crmSettingsReady,
    multipleDealsEnabled,
    leadId,
    selectedFunnelId,
    selectedLeadFunnelId,
  ])

  const refetchLeadModalAfterVkChatClose = useCallback(async () => {
    const numericLeadId = Number(leadId)
    if (!Number.isFinite(numericLeadId) || numericLeadId <= 0) {
      return
    }

    try {
      const leadDetailsResponse = await crmApi.getLead({
        lead_id: numericLeadId,
      })
      const fullLeadFromDetails =
        parseFullLeadFromGetLeadResponse(leadDetailsResponse)
      const vkMetaFromResponse =
        extractVkMetaFromGetLeadResponse(leadDetailsResponse)

      setLead((prev) => {
        if (!prev) {
          return prev
        }
        const nextLead =
          fullLeadFromDetails != null
            ? mergeLeadProfileFromGetLeadOnly(prev, fullLeadFromDetails)
            : prev

        const payload = leadDetailsResponse?.data?.data
        if (payload == null) {
          if (vkMetaFromResponse === undefined) {
            return nextLead
          }
          return patchLeadVkChatMeta(
            nextLead,
            vkMetaFromResponse as any,
          ) as LeadProfileLead
        }

        return withLeadVkChatFromPayload(nextLead, payload) as LeadProfileLead
      })
    } catch (error) {
      console.error('Failed to refetch lead after VK chat close:', error)
    }
  }, [leadId, setLead])

  const refetchLeadHardAfterMutation = useCallback(async () => {
    const fid = resolvedInitialFunnelId
    await handleRefetchLeadInModal(fid)
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 500)
    })
    await handleRefetchLeadInModal(fid)
  }, [handleRefetchLeadInModal, resolvedInitialFunnelId])

  useEffect(() => {
    if (!open) {
      bootstrapRefetchKeyRef.current = ''
      return
    }
    if (!crmSettingsReady) return
    if (leadId == null || !Number.isFinite(Number(leadId)) || Number(leadId) <= 0) {
      return
    }
    if (!lead) return

    const fid = resolvedInitialFunnelId
    if (fid === null) {
      const hasFunnelCandidate =
        toValidFunnelId(lead.currentFunnelId) != null ||
        toValidFunnelId(
          (lead as { funnel?: { id?: number | null } | null }).funnel?.id,
        ) != null ||
        (Array.isArray(lead.funnels) &&
          lead.funnels.some((f) => toValidFunnelId(f.id) != null))
      if (hasFunnelCandidate) return
    }

    if (multipleDealsEnabled) {
      const fromLead = parsePositiveId(
        (lead as { lead_funnel_id?: number | null }).lead_funnel_id,
      )
      if (openIntentLeadFunnelIdRef.current == null && fromLead != null) {
        openIntentLeadFunnelIdRef.current = fromLead
        selectedLeadFunnelIdRef.current = fromLead
      }
    }

    const dealId = multipleDealsEnabled
      ? openIntentLeadFunnelIdRef.current
      : null
    const key = `${leadId}:${fid ?? 'null'}:${dealId ?? 'null'}`
    if (bootstrapRefetchKeyRef.current === key) return
    bootstrapRefetchKeyRef.current = key

    if (fid != null && dealId != null) {
      cardFunnelDealFetchKeyRef.current = `${Number(leadId)}:${fid}:${dealId}`
    }

    void handleRefetchRef.current(fid)
  }, [
    open,
    crmSettingsReady,
    resolvedInitialFunnelId,
    leadId,
    multipleDealsEnabled,
    lead?.id,
    lead?.currentFunnelId,
    lead?.lead_funnel_id,
  ])

  useLeadRefetchRealtime({
    open,
    leadId,
    setLead,
    refetchLeadInModal: handleRefetchLeadInModal,
    resolvedFunnelId: selectedFunnelId ?? resolvedInitialFunnelId,
  })

  /** UI выбрал воронку → обновляем id и тянем `card` + `card-funnel`, чтобы снимки совпали с сервером. */
  const selectFunnel = useCallback(
    (funnelId: number | null) => {
      // Уже на этой воронке — не дергаем полный refetch (иначе мигание при mount).
      if (
        funnelId != null &&
        selectedFunnelIdRef.current != null &&
        Number(funnelId) === Number(selectedFunnelIdRef.current)
      ) {
        return
      }

      selectedFunnelIdRef.current = funnelId
      setSelectedFunnelId(funnelId)

      if (multipleDealsEnabledRef.current) {
        const list = leadCardSnapshotRef.current?.current_funnels
        const currentFunnels = Array.isArray(list) ? list : []
        const currentDealId = selectedLeadFunnelIdRef.current
        const currentDealStillInFunnel =
          currentDealId != null &&
          funnelId != null &&
          currentFunnels.some(
            (funnel) =>
              getLeadFunnelRowId(funnel) === currentDealId &&
              Number(funnel.funnel_id) === Number(funnelId),
          )

        // Смена воронки не должна сбрасывать уже выбранную сделку этой воронки
        // (в т.ч. после открытия строки таблицы с конкретным lead_funnel_id).
        const preferred = currentDealStillInFunnel
          ? currentFunnels.find(
              (funnel) => getLeadFunnelRowId(funnel) === currentDealId,
            )
          : funnelId != null
            ? currentFunnels.find(
                (funnel) => Number(funnel.funnel_id) === Number(funnelId),
              )
            : undefined

        if (funnelId != null) {
          const nextLeadFunnelId = preferred
            ? getLeadFunnelRowId(preferred)
            : currentDealStillInFunnel
              ? currentDealId
              : currentFunnels[0]
                ? getLeadFunnelRowId(currentFunnels[0])
                : currentDealId
          selectedLeadFunnelIdRef.current = nextLeadFunnelId
          setSelectedLeadFunnelId(nextLeadFunnelId)

          const numericLeadId = Number(leadId)
          if (
            Number.isFinite(numericLeadId) &&
            numericLeadId > 0 &&
            nextLeadFunnelId != null
          ) {
            cardFunnelDealFetchKeyRef.current = `${numericLeadId}:${funnelId}:${nextLeadFunnelId}`
          }
        }
        // funnelId == null: воронку сняли, сделку не трогаем — иначе теряем
        // lead_funnel_id из seed до повторного selectFunnel(id).
      }

      if (
        leadId &&
        funnelId !== null &&
        Number.isFinite(Number(funnelId)) &&
        Number(funnelId) > 0
      ) {
        void handleRefetchRef.current?.(funnelId)
      }
    },
    [leadId],
  )

  /** UI выбрал сделку → обновляем lead_funnel_id и тянем card-funnel по сделке. */
  const selectLeadFunnel = useCallback(
    (
      leadFunnelId: number | null,
      options?: { funnelId?: number | null },
    ) => {
      selectedLeadFunnelIdRef.current = leadFunnelId
      setSelectedLeadFunnelId(leadFunnelId)
      openIntentLeadFunnelIdRef.current = leadFunnelId

      if (leadFunnelId != null) {
        const list = leadCardSnapshotRef.current?.current_funnels
        const currentFunnels = Array.isArray(list) ? list : []
        const row = currentFunnels.find(
          (funnel) => getLeadFunnelRowId(funnel) === Number(leadFunnelId),
        )
        const rowFunnelId =
          row != null
            ? toValidFunnelId(row.funnel_id)
            : toValidFunnelId(options?.funnelId)
        if (
          rowFunnelId != null &&
          rowFunnelId !== selectedFunnelIdRef.current
        ) {
          selectedFunnelIdRef.current = rowFunnelId
          setSelectedFunnelId(rowFunnelId)
        }
      }

      const funnelId = selectedFunnelIdRef.current
      const numericLeadId = Number(leadId)
      if (
        Number.isFinite(numericLeadId) &&
        numericLeadId > 0 &&
        funnelId != null &&
        leadFunnelId != null
      ) {
        cardFunnelDealFetchKeyRef.current = `${numericLeadId}:${funnelId}:${leadFunnelId}`
      }

      if (
        leadId &&
        funnelId !== null &&
        Number.isFinite(Number(funnelId)) &&
        Number(funnelId) > 0
      ) {
        void handleRefetchRef.current?.(funnelId)
      }
    },
    [leadId],
  )

  const patchLead = useCallback((partial: Partial<LeadProfileLead>) => {
    setLead((prev) =>
      prev ? ({ ...prev, ...partial } as LeadProfileLead) : prev,
    )
  }, [setLead])

  const {
    committedManagerId,
    committedNextContactAt,
    committedCurrentStatusId,
    committedComment,
    committedEnteredAt,
  } = useMemo(
    () =>
      deriveCommittedFunnelSlice(
        selectedFunnelId,
        cardFunnelSnapshots,
        leadCardSnapshot,
        multipleDealsEnabled ? selectedLeadFunnelId : null,
      ),
    [
      selectedFunnelId,
      cardFunnelSnapshots,
      leadCardSnapshot,
      multipleDealsEnabled,
      selectedLeadFunnelId,
    ],
  )

  const resolveLeadFunnelId = useCallback(
    (params?: {
      lead?: LeadProfileLead | null
      selectedFunnelId?: number | null
    }) => {
      if (
        multipleDealsEnabled &&
        selectedLeadFunnelId != null &&
        Number.isFinite(selectedLeadFunnelId) &&
        selectedLeadFunnelId > 0
      ) {
        return selectedLeadFunnelId
      }

      const L = params?.lead !== undefined ? params.lead : lead
      const S =
        params?.selectedFunnelId !== undefined
          ? params.selectedFunnelId
          : selectedFunnelId
      if (!L) return undefined
      return getShoppingCurrentLeadFunnelId({
        lead: L,
        selectedFunnelId: S ?? null,
      })
    },
    [lead, selectedFunnelId, multipleDealsEnabled, selectedLeadFunnelId],
  )

  const handleRefetchLeadsOnClose = useCallback(() => {
    setNeedRefetchLeads(true)
  }, [])

  const value: LeadRefetchContextType = {
    handleRefetchLeadInModal,
    refetchLeadModalAfterVkChatClose,
    refetchLeadHardAfterMutation,
    patchLead,
    lead,
    handleRefetchLeadsOnClose,
    resolveLeadFunnelId,
    isRefetchingLeadCard,
    selectedFunnelId,
    selectFunnel,
    selectedFunnelIdRef,
    multipleDealsEnabled,
    selectedLeadFunnelId,
    selectLeadFunnel,
    leadCardSnapshot,
    committedManagerId,
    committedNextContactAt,
    committedCurrentStatusId,
    committedComment,
    committedEnteredAt,
    getCardInFunnelSnapshot,
    putCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
    initialFunnelIdForRefetch,
    setInitialFunnelIdForRefetch,
    state: {
      needRefetchLeads,
      setNeedRefetchLeads,
    },
  }

  return (
    <LeadRefetchContext.Provider value={value}>
      {children}
    </LeadRefetchContext.Provider>
  )
}
